import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {Tooltip as MantineTooltip} from '@mantine/core';
import {useId} from '@mantine/hooks';
import {StyleSheet, css} from 'aphrodite';
import React, {useCallback, useMemo, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {ELN_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isAgentTypeEnabled} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import AICheckerResults from 'notebook/ai-tools/AICheckerResults';
import {fixCheckResultsWithAssistant} from 'notebook/ai-tools/fixCheckResultsWithAssistant';
import {CheckEntryResponseState} from 'notebook/ai-tools/NotebookAIToolsConstants';
import useCheckEntryResultInfo from 'notebook/ai-tools/useCheckEntryResultInfo';
import {FONT_PROXIMA_16, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {ZINDEX_FLOATING_UI} from 'styles/StyleConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

const FULL_ACTIONS_LOCKED_TOOLTIP = 'An admin needs to grant access before full actions mode can be used.';

interface AICheckerResultsContainerProps {
  entryId: string;
  // Whether the entry can currently be edited. Sourced from the EntryStore by ReviewTabConnector so this
  // component stays presentational. Fix actions are disabled when false.
  canEditEntry: boolean;
  // The version id currently open in the editor, used to flag stale results. Also sourced from the connector.
  openVersionId: number | null;
}

/**
 * Top-level UI for the AI Notebook Checker results. This component is mounted within the review tab
 * and is responsible for fetching and displaying results, and sending relevant analytics events.
 *
 * See useCheckEntryResultInfo for details on the data fetching lifecycle.
 */
export default function AICheckerResultsContainer({
  entryId,
  canEditEntry,
  openVersionId,
}: AICheckerResultsContainerProps): JSX.Element {
  const {checkEntryResults, onToggleCompleted, onToggleUsefulState, onRecompute} =
    useCheckEntryResultInfo(entryId);
  const [isCollapsed, setCollapsed] = useState(false);
  const fixActionDescriptionId = useId();

  const {state} = checkEntryResults;
  const traceId = state === CheckEntryResponseState.SUCCESS ? checkEntryResults.traceId : null;
  const checkedAt = state === CheckEntryResponseState.SUCCESS ? checkEntryResults.checkedAt : null;
  const checkedVersion = state === CheckEntryResponseState.SUCCESS ? checkEntryResults.checkedVersion : null;
  const checkedAtDate = checkedAt != null ? new Date(checkedAt) : null;

  const rerunChecks = useCallback(() => {
    trackEvent(ELN_ANALYTICS_ACTIONS.RERAN_AI_NOTEBOOK_CHECKER, {
      traceId: state === CheckEntryResponseState.SUCCESS ? traceId : null,
      entryId,
    });
    onRecompute();
  }, [state, traceId, entryId, onRecompute]);

  const isFixActionEnabled =
    BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT_ENTRY_EDITING && isAgentTypeEnabled(AgentType.ASSISTANT);
  const results = state === CheckEntryResponseState.SUCCESS ? checkEntryResults.results : undefined;
  const selectedResults = useMemo(() => results?.filter((r) => r.isCompleted) ?? [], [results]);
  const resultsToFix = useMemo(
    () => (selectedResults.length > 0 ? selectedResults : (results ?? [])),
    [results, selectedResults]
  );
  const showFixAction = isFixActionEnabled && results != null && results.length > 0;
  const fixActionState = selectedResults.length > 0 ? 'fix_selected' : 'fix_all';
  const fixActionText = fixActionState === 'fix_selected' ? 'Fix selected' : 'Fix all';
  const isFullActionsLocked =
    BenchlingConfig.LLM_AGENT_APPROVALS_RELEASED && !BenchlingConfig.LLM_ENABLE_AGENT_APPROVALS;
  const isFixActionDisabled = !canEditEntry || isFullActionsLocked;
  const fixActionTooltip = isFullActionsLocked
    ? FULL_ACTIONS_LOCKED_TOOLTIP
    : !canEditEntry
      ? 'This entry cannot be edited right now'
      : null;

  const totalResultCount = results?.length ?? 0;
  const onClickFix = useCallback(() => {
    if (isFixActionDisabled) {
      return;
    }
    trackEvent(ELN_ANALYTICS_ACTIONS.CLICKED_AI_NOTEBOOK_CHECK_FIX_SELECTED, {
      traceId,
      entryId,
      buttonState: fixActionState,
      totalResultCount,
      selectedResultCount: selectedResults.length,
    });
    fixCheckResultsWithAssistant(entryId, resultsToFix).catch(() => {});
  }, [
    isFixActionDisabled,
    traceId,
    entryId,
    fixActionState,
    totalResultCount,
    selectedResults.length,
    resultsToFix,
  ]);

  const isCheckResultStale =
    checkedVersion != null && openVersionId != null && checkedVersion < openVersionId;
  return (
    <div
      className={css(styles.resultsContainer)}
      {...getTestProps({component: 'AICheckerResultsContainer', element: 'root'})}
    >
      <div className={css(styles.titleBar)}>
        <div className={css(styles.title)}>
          <h4>Notebook Check suggestions</h4>
          <BenchlingIcon icon="sparkle" className={css(styles.titleSparkle)} />
        </div>
        <div className={css(styles.expandCollapseIcon)}>
          <BenchlingIcon
            icon={isCollapsed ? 'chevron-down' : 'chevron-up'}
            altText={isCollapsed ? 'Expand' : 'Collapse'}
            onClick={() => {
              setCollapsed((collapsed) => !collapsed);
            }}
          />
        </div>
      </div>
      {!isCollapsed && (
        <>
          <div className={css(styles.titleBar)}>
            {showFixAction && <p className={css(styles.subtitle)}>Select what to fix, or fix all at once</p>}
            <div className={css(styles.flexRow, styles.statusControls)}>
              {checkedAtDate != null && !isCheckResultStale && (
                <div className={css(styles.statusMessage)}>
                  <span
                    title={checkedAtDate.toLocaleString()}
                    {...getTestProps({
                      component: 'AICheckerResultsContainer',
                      element: 'last-refreshed',
                    })}
                  >
                    Last updated{' '}
                    {checkedAtDate.toLocaleString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: 'numeric',
                    })}
                  </span>
                </div>
              )}
              {isCheckResultStale && (
                <div
                  className={css(styles.statusMessage)}
                  {...getTestProps({
                    component: 'AICheckerResultsContainer',
                    element: 'stale-indicator',
                  })}
                >
                  <BenchlingIcon icon="warning" className={css(styles.warningIcon)} />
                  <Tooltip text="Entry has been modified since the last check. Consider re-running checks for updated results.">
                    <span>Results may be outdated</span>
                  </Tooltip>
                </div>
              )}
              <Button
                size={ButtonSize.SMALL}
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                icon="refresh"
                onClick={rerunChecks}
                disabled={state === CheckEntryResponseState.LOADING}
                text="Refresh"
              />
            </div>
          </div>

          <AICheckerResults
            checkEntryResults={checkEntryResults}
            onToggleCompleted={onToggleCompleted}
            onToggleUsefulState={onToggleUsefulState}
            onRecompute={onRecompute}
            suppressCompletedStrikethrough={isFixActionEnabled}
          />

          {showFixAction && (
            <div className={css(styles.fixActionRow)}>
              <MantineTooltip
                disabled={fixActionTooltip == null}
                label={fixActionTooltip}
                multiline={true}
                width={225}
                withinPortal={true}
                zIndex={ZINDEX_FLOATING_UI}
              >
                <span
                  {...getTestProps({
                    component: 'AICheckerResultsContainer',
                    element: 'fix-action-tooltip-trigger',
                  })}
                >
                  <Button
                    aria-describedby={fixActionTooltip != null ? fixActionDescriptionId : undefined}
                    aria-disabled={isFixActionDisabled || undefined}
                    size={ButtonSize.SMALL}
                    buttonStyle={ButtonStyle.SECONDARY}
                    icon={isFullActionsLocked ? 'lock' : 'sparkle'}
                    onClick={onClickFix}
                    disabled={isFixActionDisabled}
                    text={fixActionText}
                    {...getTestProps({
                      component: 'AICheckerResultsContainer',
                      element: 'fix-selected',
                    })}
                  />
                </span>
              </MantineTooltip>
              {fixActionTooltip != null && (
                <span className={css(styles.visuallyHidden)} id={fixActionDescriptionId}>
                  {fixActionTooltip}
                </span>
              )}
            </div>
          )}

          {checkEntryResults.state === CheckEntryResponseState.SUCCESS && (
            <p className={css(styles.subtleText)}>
              This feature uses AI and may make mistakes. Always verify the results.{' '}
              <a
                href="https://help.benchling.com/hc/en-us/articles/27659453707405"
                target="_blank"
                rel="noopener noreferrer"
                className={css(styles.learnMoreLink)}
              >
                Learn more
                <BenchlingIcon icon="arrow-up-right-from-square" />
              </a>
            </p>
          )}
        </>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  resultsContainer: {
    borderColor: Color.GRAY_300,
    borderRadius: 4,
    borderStyle: 'solid',
    borderWidth: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginBottom: 8,
    padding: 16,
  },
  titleBar: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  titleSparkle: {
    color: Color.GRAPE_500,
    height: 20,
  },
  flexRow: {
    alignItems: 'center',
    display: 'flex',
    gap: 8,
  },
  expandCollapseIcon: {
    cursor: 'pointer',
    ...FONT_PROXIMA_16,
  },
  title: {
    alignItems: 'center',
    display: 'flex',
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    gap: 8,
  },
  subtitle: {
    color: Color.GRAY_600,
    margin: 0,
  },
  statusControls: {
    marginLeft: 'auto',
  },
  statusMessage: {
    alignItems: 'center',
    color: Color.GRAY_600,
    display: 'flex',
    gap: 8,
  },
  subtleText: {
    color: Color.GRAY_500,
    margin: 0,
  },
  warningIcon: {
    color: Color.ORANGE_400,
    height: 16,
  },
  learnMoreLink: {
    alignItems: 'center',
    display: 'inline-flex',
    gap: 4,
  },
  fixActionRow: {
    display: 'flex',
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: 1,
  },
});
